import { EventDoc, GroupDoc, GroupInheritanceMap } from 'types/api/changes'

export function normalizeGroupInheritanceMap(
  value?: GroupInheritanceMap | null
): GroupInheritanceMap {
  if (!value) return {}

  return Object.fromEntries(
    Object.entries(value).filter(
      ([groupId, enabled]) => groupId.length > 0 && enabled === '1'
    )
  )
}

export function groupIdsWithParentGroups(groups: GroupDoc[]): string[] {
  return groups
    .filter(
      (group) =>
        Object.keys(normalizeGroupInheritanceMap(group.ParentGroups)).length > 0
    )
    .map((group) => group._id)
    .filter((groupId): groupId is string => Boolean(groupId))
}

export function eventCalendarIds(event?: EventDoc | null): string[] {
  if (!event) return []

  return Array.from(
    new Set(
      [
        event.GroupID,
        ...(event.SupplementaryGroupsIDs ?? []),
        ...Object.keys(normalizeGroupInheritanceMap(event.inheritedGroups))
      ].filter((groupId): groupId is string => Boolean(groupId))
    )
  )
}

export function isEventInCalendar(
  event: EventDoc | null | undefined,
  groupId: string
): boolean {
  return eventCalendarIds(event).includes(groupId)
}

export function isEventInheritedInCalendar(
  event: EventDoc | null | undefined,
  groupId: string
): boolean {
  return normalizeGroupInheritanceMap(event?.inheritedGroups)[groupId] === '1'
}

export function sanitizeGroupInheritanceFields<T extends Record<string, any>>(
  group: T
): Omit<T, 'ParentGroups'> {
  const sanitized = { ...group }
  delete sanitized.ParentGroups
  return sanitized
}

export function getServerErrorCodes(data: unknown): number[] {
  if (!data || typeof data !== 'object') return []

  const responseData = data as {
    code?: number
    errors?: Array<{ code?: number }>
  }

  return [
    responseData.code,
    ...(responseData.errors ?? []).map((error) => error.code)
  ].filter((code): code is number => typeof code === 'number')
}
